import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { TableauAiComponents } from "@p0security/integrations-tableau-ai-shared/types";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { CommandDisplay, Typography } from "@p0security/ui";
import type React from "react";

import {
  type InstallContext,
  awsStoreAdminApp,
  awsTf,
  gcpStoreAdminApp,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  TableauAiComponents["credential"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<TableauAiComponents>["components"]["credential"]
    >["instructions"]
  >
>;

/** What a secret manager contributes to the credential install step. Methods,
 * not properties, let an implementation narrow to its own member. */
type CredentialIac<T extends SecretManagerType = SecretManagerType> = {
  instructions(
    context: InstallContext,
    site: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): InstallCommands;
  /** The command that stores the admin connected app, if this secret manager
   * has one */
  storeAdminApp(
    site: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string | undefined;
};

const gcpSmIac: CredentialIac = {
  instructions: (context, site, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s Tableau connector, then click "Next".',
          command: gcpTf(context, site, secretManager),
        },
      ],
    },
  }),
  storeAdminApp: gcpStoreAdminApp,
} satisfies CredentialIac<"gcp-sm">;

const awsSmIac: CredentialIac = {
  instructions: (context, site, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s Tableau connector, then click "Next".',
          command: awsTf(context, site, secretManager),
        },
      ],
    },
  }),
  storeAdminApp: awsStoreAdminApp,
} satisfies CredentialIac<"aws-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: CredentialIac = {
  instructions: () => ({ commands: {} }),
  storeAdminApp: () => undefined,
} satisfies CredentialIac<"azure-kv">;

const credentialIac: Record<SecretManagerType, CredentialIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const AdminAppSetup: React.FC<{
  id: string;
  secretManager: SecretManagerConfig;
}> = ({ id, secretManager }) => {
  const storeAdminApp = credentialIac[secretManager.type].storeAdminApp(
    id,
    secretManager
  );
  return (
    <div>
      <Paragraph type="secondary">
        In Tableau, create a connected app for P0 under Settings &gt; Connected
        Apps &gt; New Connected App &gt; Direct Trust. Generate a secret, then
        enable the app.
      </Paragraph>
      <Paragraph type="secondary">
        P0 grants access only to projects whose permissions are locked to the
        project.
      </Paragraph>
      {storeAdminApp && (
        <>
          <Paragraph>Admin connected app</Paragraph>
          <Paragraph type="secondary">
            Set <code>TABLEAU_CLIENT_ID</code>, <code>TABLEAU_SECRET_ID</code>{" "}
            and <code>TABLEAU_SECRET</code> to the app&apos;s client ID, secret
            ID and secret value, and run this command to update the secret to
            them. The connector cannot grant access until the secret holds the
            app.
          </Paragraph>
          <CommandDisplay commands={storeAdminApp} />
        </>
      )}
      <Paragraph>Admin user</Paragraph>
    </div>
  );
};

export const tableauAiInstaller: FrontendInstaller<TableauAiComponents> = {
  components: {
    credential: {
      instructions: (context, id, item) =>
        credentialIac[item.secretManager.type].instructions(
          context,
          id,
          item.secretManager
        ),
      items: {
        adminUser: {
          labeler: (_, id, { secretManager, state }) =>
            state === "configure" && (
              <AdminAppSetup id={id} secretManager={secretManager} />
            ),
        },
        secretManager: {
          options: {
            "aws-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "aws-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "gcp-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
          },
        },
      },
    },
  },
};
